import Vue from 'vue';
import Router from 'vue-router';
import Home from '@/components/LandingPage/Home';
import Catalog from '@/components/Catalog/Catalog';
import CategoryPage from '@/components/Catalog/CategoryPage';
import ProductDetail from '@/components/Products/ProductDetail';
import Kids from '@/components/Kids';
import KidDetailOverview from '@/components/Kids/KidDetailOverview';
import PointsOverview from '@/components/Points/PointsOverview';
import PointsStore from '@/components/Points/PointsStore';
import PointsPurchaseHistory from '@/components/Points/PointsPurchaseHistory';
import PointsAutoReloadSettings from '@/components/Points/PointsAutoReloadSettings';
import ParentLoginPage from '@/components/ParentLoginPage';
import Signup from '@/components/Signup';
import Logout from '@/components/Logout';
import PasswordForgot from '@/components/Password/Forgot';
import PasswordReset from '@/components/Password/Reset';
import ParentDashboard from '@/components/ParentDashboard/ParentDashboard';
import AddChild from '@/components/ParentDashboard/AddChild';
import ChildDashboard from '@/components/ChildDashboard/ChildDashboard';
import CreateMission from '@/components/Mission/CreateMission';
import MissionDetail from '@/components/Mission/MissionDetail';
import SettingOverview from '@/components/GeneralSettings/SettingOverview';
import VerifyEmailPage from '@/components/VerifyEmail/VerifyEmailPage';
import VerifyPhonePage from '@/components/VerifyPhone/VerifyPhonePage';
import CartOverview from '@/components/Cart/CartOverview';
import KidGifts from '@/components/Kids/KidGifts';
import KidGiftsOverview from '@/components/Kids/KidGiftsOverview';
import KidMissionList from '@/components/Kids/KidMissionList';
import KidProfile from '@/components/Kids/KidProfile';
import FAQPage from '@/components/FAQPage';
import PrivacyPolicyPage from '@/components/PrivacyPolicyPage';
import TermsOfServicePage from '@/components/TermsOfServicePage';
import AboutUsPage from '@/components/AboutUsPage';

import NotFound from '@/components/Errors/NotFound';
import ServerError from '@/components/Errors/ServerError';
import SessionUser from '../helpers/session-user';

Vue.use(Router);

const checkIfAuthenticated = (to, from, next) => {
  let user = SessionUser._getUserInfo();
  if (to.name === 'Home' && user.token && user.role === 'parent') {
    next({ name: 'ParentDashboard' });
  } else if (to.name === 'Home' && user.token && user.role === 'child') {
    next({ name: 'ChildDashboard' });
  } else {
    next();
  }
};

const requireParentDomain = (to, from, next) => {
  let hostName = window.location.hostname;
  if (hostName && hostName !== 'easiclub') {
    next();
  } else {
    next(false);
  }
};

const requireChild = (to, from, next) => {
  let user = SessionUser._getUserInfo();
  if (user && user.token && user.role === 'child') {
    next();
  } else {
    next(false);
  }
};

const requireParent = (to, from, next) => {
  if (localStorage.token && localStorage.role === 'parent') {
    next();
  } else {
    next(false);
  }
};

export default new Router({
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0 };
  },
  mode: 'history',
  routes: [
    {
      path: '/',
      name: 'Home',
      beforeEnter: checkIfAuthenticated,
      component: Home
    },
    {
      path: '/catalog',
      name: 'Catalog',
      component: Catalog
    },
    {
      path: '/catalog/:categoryId',
      name: 'CategoryPage',
      component: CategoryPage
    },
    {
      path: '/products/:id',
      name: 'ProductDetail',
      component: ProductDetail
    },
    {
      path: '/kids',
      name: 'Kids',
      beforeEnter: requireParent,
      component: Kids
    },
    {
      path: '/kids/:id',
      name: 'KidDetail',
      component: KidDetailOverview,
      beforeEnter: requireParent,
      children: [
        {
          path: 'missions',
          name: 'KidQuests',
          component: KidMissionList,
          beforeEnter: requireParent
        },
        {
          path: 'profile',
          name: 'KidProfile',
          component: KidProfile,
          beforeEnter: requireParent
        },
        {
          path: 'gifts',
          name: 'KidGifts',
          component: KidGifts,
          beforeEnter: requireParent
        }
      ]
    },
    {
      path: '/kids/:id/assignmission',
      name: 'CreateMission',
      beforeEnter: requireParent,
      component: CreateMission
    },
    {
      path: '/kids/:id/missions/:missionId',
      name: 'MissionDetail',
      component: MissionDetail
    },
    {
      path: '/points/',
      name: 'PointsOverview',
      component: PointsOverview,
      beforeEnter: requireParent,
      children: [
        {
          path: 'store',
          name: 'PointsStore',
          component: PointsStore,
          beforeEnter: requireParent
        },
        {
          path: 'settings',
          name: 'PointsAutoReloadSettings',
          component: PointsAutoReloadSettings,
          beforeEnter: requireParent
        },
        {
          path: 'history',
          name: 'PointsPurchaseHistory',
          component: PointsPurchaseHistory,
          beforeEnter: requireParent
        }
      ]
    },
    {
      path: '/login',
      name: 'Login',
      beforeEnter: requireParentDomain,
      component: ParentLoginPage
    },
    {
      path: '/signup',
      name: 'Signup',
      beforeEnter: requireParentDomain,
      component: Signup
    },
    {
      path: '/logout',
      name: 'Logout',
      component: Logout
    },
    {
      path: '/forgot',
      name: 'PasswordForgot',
      component: PasswordForgot
    },
    {
      path: '/reset/:token',
      name: 'PasswordReset',
      component: PasswordReset
    },
    {
      path: '/oops',
      name: 'ServerError',
      component: ServerError
    },
    {
      path: '*',
      name: 'NotFound',
      component: NotFound
    },
    {
      path: '/parentdashboard',
      name: 'ParentDashboard',
      component: ParentDashboard
    },
    {
      path: '/addchild',
      name: 'AddChild',
      beforeEnter: requireParent,
      component: AddChild
    },
    {
      path: '/childdashboard',
      name: 'ChildDashboard',
      component: ChildDashboard
    },
    {
      path: '/settings/account',
      name: 'AccountSettings',
      component: SettingOverview
    },
    {
      path: '/settings/payment',
      name: 'PaymentSettings',
      beforeEnter: requireParent,
      component: SettingOverview
    },
    {
      path: '/settings/notification',
      name: 'NotificationSettings',
      beforeEnter: requireParent,
      component: SettingOverview
    },
    {
      path: '/verify/email/:code',
      name: 'VerifyEmail',
      component: VerifyEmailPage
    },
    {
      path: '/verify/phone/:code',
      name: 'VerifyPhone',
      component: VerifyPhonePage
    },
    {
      path: '/cart',
      name: 'CartOverview',
      beforeEnter: requireChild,
      component: CartOverview
    },
    {
      path: '/gifts',
      name: 'MyGifts',
      beforeEnter: requireChild,
      component: KidGiftsOverview
    },
    {
      path: '/blog',
      name: 'Blog',
      component: () => import('@/components/Blog/Blog')
    },
    {
      path: '/blog/article/:id',
      name: 'BlogArticle',
      component: () => import('@/components/Blog/Article')
    },
    {
      path: '/faq',
      name: 'FAQPage',
      component: FAQPage
    },
    {
      path: '/privacy-policy',
      name: 'PrivacyPolicyPage',
      component: PrivacyPolicyPage
    },
    {
      path: '/terms-of-service',
      name: 'TermsOfServicePage',
      component: TermsOfServicePage
    },
    {
      path: '/about-us',
      name: 'AboutUsPage',
      component: AboutUsPage
    }
  ]
});



// WEBPACK FOOTER //
// ./src/router/index.js